import React from "react";
import classnames from "classnames";
import baseStyles from "./modals.module.scss";
import { IModalInstance } from "./ModalProvider";
import { useEffect, useRef } from "react";
import { useClickOutside } from "../componentsUtils";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IModalProps<T extends {} = any> extends IModalInstance<T> {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  className?: any;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  closeOnEscape?: boolean;
  closeOnClickOutside?: boolean;
  ignoreClickOutsideSelector?: string;
  bodyClassName?: string;
  backdropClassName?: string;
  headerClassName?: string;
  testId?: string;
  backdropHeader?: React.ReactNode;
}

const MaybeForm: React.FC<{ onSubmit?: React.FormEventHandler<HTMLFormElement>; children?: React.ReactNode }> = (props) =>
  props.onSubmit ? <form onSubmit={props.onSubmit}>{props.children}</form> : <React.Fragment>{props.children}</React.Fragment>;

const Backdrop: React.FC<{
  onEscape?: () => void;
  className?: string;
  children?: React.ReactNode;
  backdropHeader?: React.ReactNode;
}> = (props) => {
  useEffect(() => {
    if (props.onEscape) {
      const listener = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          props.onEscape?.();
        }
      };
      document.addEventListener("keydown", listener, { capture: true });
      return () => document.removeEventListener("keydown", listener, { capture: true });
    }
    return undefined;
  }, [props, props.onEscape]);

  return (
    <div className={classnames(baseStyles.modalContainer, props.className)}>
      {props.backdropHeader}
      {props.children}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const BaseModal = <T extends {} = any>({
  closeOnEscape = true,
  closeOnClickOutside = true,
  ...props
}: IModalProps<T> & { children?: React.ReactNode }) => {
  const modalRef = useClickOutside<HTMLDivElement>({
    callback: props.closeModal,
    activate: Boolean(closeOnClickOutside),
    ignoreTargetSelector: props.ignoreClickOutsideSelector,
  });

  return (
    <Backdrop className={props.backdropClassName} onEscape={closeOnEscape ? props.closeModal : undefined} backdropHeader={props.backdropHeader}>
      <div ref={modalRef} className={classnames(baseStyles.modal, props.className ?? "")} data-testid={props.testId}>
        <MaybeForm onSubmit={props.onSubmit}>
          {props.header ? <div className={classnames(baseStyles.header, props.headerClassName)}>{props.header}</div> : ""}
          <div className={classnames(baseStyles.body, props.bodyClassName)}>{props.children}</div>
          {props.footer ? <div className={baseStyles.footer}>{props.footer}</div> : ""}
        </MaybeForm>
      </div>
    </Backdrop>
  );
};
